import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { COUNTRY_OPTIONS } from "@/lib/countries";

interface CountryComboboxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

export default function CountryCombobox({
  value,
  onChange,
  placeholder = "No default — auto-detect",
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? COUNTRY_OPTIONS.find((c) => c.code === value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">
              {selected.emoji} {selected.name} (+{selected.dialCode})
            </span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {selected && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country or dial code..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__ no default auto detect"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === null ? "opacity-100" : "opacity-0"
                  )}
                />
                No default (auto-detect)
              </CommandItem>
              {COUNTRY_OPTIONS.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code} +${c.dialCode}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === c.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="mr-2">{c.emoji}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="ml-2 text-muted-foreground">+{c.dialCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
